import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getThemeClassNames, MIRRORS_THEMES } from 'themes';

import { MirrorService } from 'services/MirrorService';
import { AppDomain } from 'types/enums/AppDomain';

import {
  getHasNotifications,
  getNewDialogsCount,
} from 'store/common/selectors';

import { ReactComponent as NotificationsIcon } from 'assets/icons/activities.svg';
import { ReactComponent as DialogsIcon } from 'assets/icons/dialogs.svg';
import { ReactComponent as MyProfileIcon } from 'assets/icons/myprofile.svg';
import { ReactComponent as FinderIcon } from 'assets/icons/search.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';

import styles from './headerMenu.module.sass';

type PropTypes = {
  className?: string;
  disabled?: boolean;
};

export const SidebarMenu: React.FC<PropTypes> = ({ className, disabled }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const newDialogsCount = useSelector(getNewDialogsCount);
  const hasNotifications = useSelector(getHasNotifications);

  const activeColor = MIRRORS_THEMES[MirrorService.domain]?.accent;

  const nonActiveColor = useMemo(() => {
    if (MirrorService.isSweatHeartSoul) {
      return '#000000';
    }

    if (!MirrorService.isLocalsDate) {
      return '#ffffff';
    }

    return MIRRORS_THEMES[MirrorService.domain]?.primary;
  }, []);

  const btnClassName = getThemeClassNames(styles.navBtn, {
    [AppDomain.LocalsDate]: styles.localsDateColorTheme,
    [AppDomain.SweatHeartSoul]: styles.dark,
  });

  return (
    <nav
      className={cx(styles.wrapper, className, { [styles.disabled]: disabled })}
    >
      <ul className={styles.list}>
        <li className={styles.listItem}>
          <BaseButton
            type={ButtonTypes.Transparent}
            to="/finder"
            className={cx(btnClassName, {
              [styles.active]: location.pathname === '/finder',
            })}
          >
            <FinderIcon
              fill={
                location.pathname === '/finder' ? activeColor : nonActiveColor
              }
              stroke={
                location.pathname === '/finder' ? activeColor : nonActiveColor
              }
              className={styles.navIcon}
            />
            <span className={styles.navText}>{t('sidebar.finder')}</span>
          </BaseButton>
        </li>

        <li className={styles.listItem}>
          <BaseButton
            type={ButtonTypes.Transparent}
            to="/threads"
            className={cx(btnClassName, {
              [styles.active]: location.pathname.includes('/threads'),
              [styles.withMargin]: newDialogsCount,
            })}
          >
            <DialogsIcon
              fill={
                location.pathname.includes('/threads')
                  ? activeColor
                  : nonActiveColor
              }
              className={styles.navIcon}
            />
            <span className={styles.navText}>{t('sidebar.messages')}</span>
          </BaseButton>

          {!!newDialogsCount && <div className={styles.redDot} />}
        </li>

        <li className={styles.listItem}>
          <BaseButton
            type={ButtonTypes.Transparent}
            to="/activities/notifications"
            className={cx(btnClassName, {
              [styles.active]:
                location.pathname === '/activities/notifications',
              [styles.withMargin]: hasNotifications,
            })}
          >
            <NotificationsIcon
              fill={
                location.pathname === '/activities/notifications'
                  ? activeColor
                  : nonActiveColor
              }
              className={styles.navIcon}
            />
            <span className={styles.navText}>{t('sidebar.notifications')}</span>
          </BaseButton>

          {hasNotifications && <div className={styles.redDot} />}
        </li>

        <li className={styles.listItem}>
          <BaseButton
            type={ButtonTypes.Transparent}
            to="/myprofile"
            className={cx(btnClassName, {
              [styles.active]: location.pathname === '/myprofile',
            })}
          >
            <MyProfileIcon
              fill={
                location.pathname === '/myprofile'
                  ? activeColor
                  : nonActiveColor
              }
              className={styles.navIcon}
            />
            <span className={styles.navText}>{t('sidebar.myProfile')}</span>
          </BaseButton>
        </li>
      </ul>
    </nav>
  );
};
